<template>
  <div class="skill-admin-page">
    <a-tabs v-model:activeKey="activeTab">
      <a-tab-pane key="skills" tab="技能管理">
        <div class="toolbar">
          <a-input-search
            v-model:value="query.keyword"
            class="search-input"
            placeholder="搜索技能名称、标识或简介"
            allow-clear
            @search="loadSkills(1)"
          />
          <a-select v-model:value="query.status" class="filter-item" placeholder="状态" allow-clear @change="loadSkills(1)">
            <a-select-option value="draft">草稿</a-select-option>
            <a-select-option value="published">已发布</a-select-option>
            <a-select-option value="disabled">已下架</a-select-option>
          </a-select>
          <a-button type="primary" @click="loadSkills(1)">
            <template #icon><SearchOutlined /></template>
            查询
          </a-button>
          <a-button @click="openCreate">
            <template #icon><PlusOutlined /></template>
            新建技能
          </a-button>
        </div>

        <a-table
          :columns="columns"
          :data-source="skills"
          :loading="loading"
          :pagination="pagination"
          row-key="id"
          @change="handleTableChange"
        >
          <template #name="{ record }">
            <div class="name-cell">
              <a @click="openVersions(record)">{{ record.name }}</a>
              <div>{{ record.slug }}</div>
            </div>
          </template>
          <template #status="{ record }">
            <a-tag :color="statusColor(record.status)">{{ statusText(record.status) }}</a-tag>
          </template>
          <template #agents="{ record }">
            <a-tag v-for="agent in record.supportedAgents || []" :key="agent">{{ agentText(agent) }}</a-tag>
          </template>
          <template #action="{ record }">
            <a-space>
              <a-button size="small" @click="openEdit(record)">编辑</a-button>
              <a-button size="small" @click="openUpload(record)">上传版本</a-button>
              <a-button size="small" @click="openVersions(record)">版本</a-button>
              <a-button size="small" danger @click="removeSkill(record)">删除</a-button>
            </a-space>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="logs" tab="下载记录">
        <div class="toolbar">
          <a-input-search
            v-model:value="logQuery.keyword"
            class="search-input"
            placeholder="搜索技能、版本或下载人"
            allow-clear
            @search="loadLogs(1)"
          />
          <a-button type="primary" @click="loadLogs(1)">
            <template #icon><SearchOutlined /></template>
            查询
          </a-button>
        </div>
        <a-table
          :columns="logColumns"
          :data-source="logs"
          :loading="logLoading"
          :pagination="logPagination"
          row-key="id"
          @change="handleLogTableChange"
        />
      </a-tab-pane>
    </a-tabs>

    <a-modal
      v-model:visible="editVisible"
      :title="editForm.id ? '编辑技能' : '新建技能'"
      width="760px"
      :confirm-loading="saving"
      :mask-closable="false"
      @ok="saveSkill"
    >
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-item label="技能标识" required>
          <a-input v-model:value="editForm.slug" placeholder="titan-ad" />
        </a-form-item>
        <a-form-item label="技能名称" required>
          <a-input v-model:value="editForm.name" placeholder="泰坦广告投放查询" />
        </a-form-item>
        <a-form-item label="简介" required>
          <a-textarea v-model:value="editForm.summary" :rows="2" :maxlength="300" show-count />
        </a-form-item>
        <a-form-item label="分类" required>
          <a-select v-model:value="editForm.category">
            <a-select-option v-for="item in categories" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="介绍">
          <a-textarea v-model:value="editForm.descriptionMd" :rows="6" />
        </a-form-item>
        <a-form-item label="可见范围">
          <a-radio-group v-model:value="scopeType">
            <a-radio-button value="all">全公司</a-radio-button>
            <a-radio-button value="dept">部门</a-radio-button>
            <a-radio-button value="role">角色</a-radio-button>
            <a-radio-button value="user">人员</a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item v-if="scopeType === 'dept'" label="选择部门">
          <a-tree-select
            v-model:value="scopeIds"
            :tree-data="departmentData"
            :replace-fields="{ children: 'list', title: 'name', key: 'deptId', value: 'deptId' }"
            tree-checkable
            tree-node-filter-prop="title"
            allow-clear
            show-search
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item v-if="scopeType === 'role'" label="选择角色">
          <a-select v-model:value="scopeIds" mode="multiple" allow-clear show-search option-filter-prop="label" style="width: 100%">
            <a-select-option v-for="role in roles" :key="role.roleId" :value="role.roleId" :label="role.roleName">
              {{ role.roleName }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item v-if="scopeType === 'user'" label="选择人员">
          <a-select
            v-model:value="scopeIds"
            mode="multiple"
            allow-clear
            show-search
            :filter-option="false"
            :options="userOptions"
            @search="searchUsers"
            style="width: 100%"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model:visible="uploadVisible"
      title="上传Skill版本"
      width="620px"
      :confirm-loading="uploading"
      :mask-closable="false"
      @ok="submitUpload"
    >
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-item label="技能" required>
          <a-select v-model:value="uploadForm.skillId" :disabled="!!uploadForm.skillId" option-filter-prop="label" show-search>
            <a-select-option v-for="skill in skills" :key="skill.id" :value="skill.id" :label="skill.name">
              {{ skill.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="版本号" required>
          <a-input v-model:value="uploadForm.version" placeholder="0.1.0" />
        </a-form-item>
        <a-form-item label="支持平台">
          <a-checkbox-group v-model:value="uploadForm.supportedAgents">
            <a-checkbox v-for="(label, value) in agents" :key="value" :value="value">{{ label }}</a-checkbox>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item label="Skill包" required>
          <a-upload
            :file-list="fileList"
            accept=".zip"
            :max-count="1"
            :before-upload="beforeUpload"
            @remove="removeFile"
          >
            <a-button>
              <template #icon><UploadOutlined /></template>
              选择zip包
            </a-button>
          </a-upload>
        </a-form-item>
      </a-form>
    </a-modal>

    <a-drawer v-model:visible="versionVisible" title="版本管理" width="860px">
      <a-button class="drawer-action" type="primary" @click="openUpload(currentSkill)">
        <template #icon><PlusOutlined /></template>
        上传新版本
      </a-button>
      <a-table :columns="versionColumns" :data-source="currentVersions" row-key="id" :pagination="false">
        <template #status="{ record }">
          <a-tag :color="statusColor(record.status)">{{ statusText(record.status) }}</a-tag>
        </template>
        <template #size="{ record }">{{ formatSize(record.packageSize) }}</template>
        <template #warnings="{ record }">{{ (record.securityWarnings || []).join('；') || '-' }}</template>
        <template #action="{ record }">
          <a-space>
            <a-button size="small" @click="openVersionEdit(record)">编辑</a-button>
            <a-button v-if="record.status !== 'published'" size="small" type="primary" @click="publishVersion(record)">
              发布
            </a-button>
            <a-button v-else size="small" danger @click="disableVersion(record)">下架</a-button>
          </a-space>
        </template>
      </a-table>
    </a-drawer>

    <a-modal
      v-model:visible="versionEditVisible"
      title="编辑版本信息"
      width="760px"
      :confirm-loading="versionSaving"
      :mask-closable="false"
      @ok="saveVersion"
    >
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-item label="版本号" required>
          <a-input v-model:value="versionEditForm.version" />
        </a-form-item>
        <a-form-item label="支持平台">
          <a-checkbox-group v-model:value="versionEditForm.supportedAgents">
            <a-checkbox v-for="(label, value) in agents" :key="value" :value="value">{{ label }}</a-checkbox>
          </a-checkbox-group>
        </a-form-item>
        <a-form-item label="安装说明">
          <a-textarea v-model:value="versionEditForm.installGuideMd" :rows="4" />
        </a-form-item>
        <a-form-item label="安装Prompt">
          <a-textarea v-model:value="versionEditForm.installPromptMd" :rows="8" />
          <div class="form-help">
            可使用占位符：${installUrl} 自动替换为30分钟有效的下载地址，${packageSha256} 自动替换为包校验值。未填写时会使用系统默认安装Prompt。
          </div>
        </a-form-item>
        <a-form-item label="更新日志">
          <a-textarea v-model:value="versionEditForm.changelogMd" :rows="4" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { message, Modal } from 'ant-design-vue';
import { createVNode } from 'vue';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons-vue';
import {
  deleteSkill,
  disableSkillVersion,
  getAdminSkillList,
  getSkillDetail,
  getSkillDownloadLogs,
  publishSkillVersion,
  saveSkill,
  updateSkillVersion,
  uploadSkillVersion,
} from '@/api/skill';
import { getDepartmentData } from '@/api/org/account';
import { getUserListData } from '@/api/systemManage/userManage';
import { getRoleSelectData } from '@/api/systemManage/roleManage';

export default {
  components: { DeleteOutlined, PlusOutlined, SearchOutlined, UploadOutlined },
  data() {
    return {
      activeTab: 'skills',
      loading: false,
      logLoading: false,
      saving: false,
      uploading: false,
      versionSaving: false,
      skills: [],
      logs: [],
      query: { keyword: '', status: undefined },
      logQuery: { keyword: '' },
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true },
      logPagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true },
      editVisible: false,
      uploadVisible: false,
      versionVisible: false,
      versionEditVisible: false,
      scopeType: 'all',
      scopeIds: [],
      editForm: {},
      uploadForm: { skillId: undefined, version: '', supportedAgents: ['codex', 'claude', 'openclaw', 'workbuddy', 'hermes'] },
      fileList: [],
      currentSkill: null,
      currentVersions: [],
      versionEditForm: {},
      departmentData: [],
      roles: [],
      userOptions: [],
      categories: [
        { label: '通用', value: 'general' },
        { label: '投放查询', value: 'advertising' },
        { label: '数据分析', value: 'data' },
        { label: '内容创作', value: 'content' },
        { label: '办公效率', value: 'productivity' },
      ],
      agents: {
        codex: 'Codex',
        claude: 'Claude',
        openclaw: 'OpenClaw',
        workbuddy: 'WorkBuddy',
        hermes: 'Hermes',
      },
      columns: [
        { title: '技能', dataIndex: 'name', key: 'name', width: 260, slots: { customRender: 'name' } },
        { title: '状态', dataIndex: 'status', key: 'status', width: 100, slots: { customRender: 'status' } },
        { title: '最新版本', dataIndex: 'latestVersion', key: 'latestVersion', width: 100 },
        { title: '支持平台', dataIndex: 'supportedAgents', key: 'agents', width: 230, slots: { customRender: 'agents' } },
        { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 170 },
        { title: '下载次数', dataIndex: 'downloadCount', key: 'downloadCount', width: 100 },
        { title: '操作', key: 'action', width: 310, slots: { customRender: 'action' } },
      ],
      logColumns: [
        { title: '技能', dataIndex: 'skillName', key: 'skillName', width: 220 },
        { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
        { title: '下载人', dataIndex: 'userName', key: 'userName', width: 130 },
        { title: 'IP', dataIndex: 'ip', key: 'ip', width: 150 },
        { title: '下载时间', dataIndex: 'downloadedAt', key: 'downloadedAt', width: 180 },
      ],
      versionColumns: [
        { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
        { title: '状态', dataIndex: 'status', key: 'status', width: 100, slots: { customRender: 'status' } },
        { title: '大小', dataIndex: 'packageSize', key: 'size', width: 100, slots: { customRender: 'size' } },
        { title: '发布时间', dataIndex: 'publishedAt', key: 'publishedAt', width: 170 },
        { title: '安全提示', dataIndex: 'securityWarnings', key: 'warnings', slots: { customRender: 'warnings' } },
        { title: '操作', key: 'action', width: 170, slots: { customRender: 'action' } },
      ],
    };
  },
  mounted() {
    this.loadSkills();
    this.loadBaseOptions();
  },
  methods: {
    async loadSkills(page) {
      if (page) this.pagination.current = page;
      this.loading = true;
      try {
        const res = await getAdminSkillList({
          page: this.pagination.current,
          limit: this.pagination.pageSize,
          ...this.query,
        });
        if (res.code === 0) {
          this.skills = res.page?.list || [];
          this.pagination.total = res.page?.totalCount || 0;
        }
      } finally {
        this.loading = false;
      }
    },
    async loadLogs(page) {
      if (page) this.logPagination.current = page;
      this.logLoading = true;
      try {
        const res = await getSkillDownloadLogs({
          page: this.logPagination.current,
          limit: this.logPagination.pageSize,
          ...this.logQuery,
        });
        if (res.code === 0) {
          this.logs = res.page?.list || [];
          this.logPagination.total = res.page?.totalCount || 0;
        }
      } finally {
        this.logLoading = false;
      }
    },
    async loadBaseOptions() {
      const [deptRes, roleRes, userRes] = await Promise.all([
        getDepartmentData(),
        getRoleSelectData(),
        getUserListData({ page: 1, limit: 50 }),
      ]);
      if (deptRes.code === 0) this.departmentData = deptRes.deptList || [];
      if (roleRes.code === 0) this.roles = roleRes.list || [];
      if (userRes.code === 0) {
        this.userOptions = (userRes.page?.list || []).map(user => ({
          value: user.userId,
          label: `${user.name}(${user.userCode || user.userId})`,
        }));
      }
    },
    async searchUsers(keyword) {
      const res = await getUserListData({ page: 1, limit: 50, name: keyword });
      if (res.code === 0) {
        this.userOptions = (res.page?.list || []).map(user => ({
          value: user.userId,
          label: `${user.name}(${user.userCode || user.userId})`,
        }));
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadSkills();
    },
    handleLogTableChange(pagination) {
      this.logPagination.current = pagination.current;
      this.logPagination.pageSize = pagination.pageSize;
      this.loadLogs();
    },
    openCreate() {
      this.editVisible = true;
      this.editForm = { category: 'general' };
      this.scopeType = 'all';
      this.scopeIds = [];
    },
    async openEdit(record) {
      const res = await getSkillDetail(record.id);
      if (res.code !== 0) return;
      const detail = res.data;
      this.editVisible = true;
      this.editForm = { ...detail };
      const firstScope = detail.scopes?.[0];
      this.scopeType = firstScope?.scopeType || 'all';
      this.scopeIds = detail.scopes?.map(item => item.scopeId).filter(Boolean) || [];
    },
    async saveSkill() {
      this.saving = true;
      try {
        const scopes = this.scopeType === 'all'
          ? [{ scopeType: 'all' }]
          : this.scopeIds.map(id => ({ scopeType: this.scopeType, scopeId: id }));
        const res = await saveSkill({ ...this.editForm, scopes });
        if (res.code === 0) {
          message.success('保存成功');
          this.editVisible = false;
          this.loadSkills();
        }
      } finally {
        this.saving = false;
      }
    },
    openUpload(record) {
      this.uploadVisible = true;
      this.uploadForm = {
        skillId: record?.id,
        version: '',
        supportedAgents: ['codex', 'claude', 'openclaw', 'workbuddy', 'hermes'],
      };
      this.fileList = [];
    },
    beforeUpload(file) {
      this.fileList = [{ uid: file.uid, name: file.name, status: 'done', originFileObj: file }];
      return false;
    },
    removeFile() {
      this.fileList = [];
    },
    async submitUpload() {
      const file = this.fileList[0]?.originFileObj;
      if (!file) {
        message.warning('请选择Skill zip包');
        return;
      }
      this.uploading = true;
      try {
        const res = await uploadSkillVersion({ ...this.uploadForm, file });
        if (res.code === 0) {
          message.success('版本上传成功，确认信息后可发布');
          this.uploadVisible = false;
          this.loadSkills();
          if (this.currentSkill?.id === this.uploadForm.skillId) {
            await this.openVersions(this.currentSkill);
          }
        }
      } finally {
        this.uploading = false;
      }
    },
    async openVersions(record) {
      const res = await getSkillDetail(record.id);
      if (res.code !== 0) return;
      this.currentSkill = record;
      this.currentVersions = res.data.versions || [];
      this.versionVisible = true;
    },
    openVersionEdit(record) {
      this.versionEditVisible = true;
      this.versionEditForm = { ...record };
    },
    async saveVersion() {
      this.versionSaving = true;
      try {
        const res = await updateSkillVersion(this.versionEditForm);
        if (res.code === 0) {
          message.success('版本信息已保存');
          this.versionEditVisible = false;
          await this.openVersions(this.currentSkill);
          this.loadSkills();
        }
      } finally {
        this.versionSaving = false;
      }
    },
    async publishVersion(record) {
      const res = await publishSkillVersion(record.id);
      if (res.code === 0) {
        message.success('版本已发布');
        await this.openVersions(this.currentSkill);
        this.loadSkills();
      }
    },
    async disableVersion(record) {
      const res = await disableSkillVersion(record.id);
      if (res.code === 0) {
        message.success('版本已下架');
        await this.openVersions(this.currentSkill);
        this.loadSkills();
      }
    },
    removeSkill(record) {
      Modal.confirm({
        title: '删除技能',
        icon: createVNode(ExclamationCircleOutlined),
        content: `确认删除「${record.name}」吗？已下载的包不受影响，平台将不再展示该技能。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          const res = await deleteSkill(record.id);
          if (res.code === 0) {
            message.success('已删除');
            this.loadSkills();
          }
        },
      });
    },
    statusText(value) {
      return { draft: '草稿', published: '已发布', disabled: '已下架' }[value] || value;
    },
    statusColor(value) {
      return { draft: 'orange', published: 'green', disabled: 'red' }[value] || 'default';
    },
    agentText(value) {
      return this.agents[value] || value;
    },
    formatSize(value) {
      if (!value) return '-';
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
  },
};
</script>

<style lang="less" scoped>
.skill-admin-page {
  padding: 20px;
  background: #fff;
  min-height: 100%;
}

.form-help {
  color: #6b7280;
  font-size: 12px;
  line-height: 20px;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.search-input {
  width: 320px;
}

.filter-item {
  width: 140px;
}

.name-cell {
  div {
    margin-top: 3px;
    color: #6b7280;
    font-size: 12px;
  }
}

.drawer-action {
  margin-bottom: 14px;
}
</style>
